import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthContext } from '../context/AuthContext';

/**
 * Check the friendship status between the current user and a target user
 * Returns: 'self' | 'friends' | 'request_sent' | 'request_received' | 'none'
 */
export function useFriendshipStatus(targetUid) {
  const { user } = useAuthContext();
  const [status, setStatus] = useState('none');
  const [requestId, setRequestId] = useState(null);
  const [friendshipId, setFriendshipId] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkStatus = useCallback(async () => {
    if (!user || !targetUid) {
      setStatus('none');
      setLoading(false);
      return;
    }

    // Self check
    if (user.uid === targetUid) {
      setStatus('self');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Check if already friends (deterministic doc ID)
      const detId = [user.uid, targetUid].sort().join('_');
      const friendRef = doc(db, 'friends', detId);
      const friendSnap = await getDoc(friendRef);

      if (friendSnap.exists()) {
        setStatus('friends');
        setFriendshipId(detId);
        setLoading(false);
        return;
      }

      // Check if I sent a request to them
      const sentQuery = query(
        collection(db, 'friendRequests'),
        where('from', '==', user.uid),
        where('to', '==', targetUid)
      );
      const sentSnap = await getDocs(sentQuery);

      if (!sentSnap.empty) {
        setStatus('request_sent');
        setRequestId(sentSnap.docs[0].id);
        setLoading(false);
        return;
      }

      // Check if they sent a request to me
      const receivedQuery = query(
        collection(db, 'friendRequests'),
        where('from', '==', targetUid),
        where('to', '==', user.uid)
      );
      const receivedSnap = await getDocs(receivedQuery);

      if (!receivedSnap.empty) {
        setStatus('request_received');
        setRequestId(receivedSnap.docs[0].id);
        setLoading(false);
        return;
      }

      // No relationship
      setStatus('none');
      setRequestId(null);
      setFriendshipId(null);
    } catch (err) {
      console.error('Error checking friendship status:', err);
      setStatus('none');
    } finally {
      setLoading(false);
    }
  }, [user, targetUid]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  return { status, loading, requestId, friendshipId, refetch: checkStatus };
}
